import type {
  ExplorerAction,
  ExplorerControlState,
  ExplorerControlsController,
  ExplorerControlsOptions
} from './types.js';

type Off = () => void;

export function createExplorerControls(options: ExplorerControlsOptions = {}): ExplorerControlsController {
  const joystick = options.joystick ?? null;
  const knob = options.knob ?? joystick?.querySelector<HTMLElement>('i') ?? null;
  const actionButton = options.actionButton ?? null;
  const activeClass = options.activeClass ?? 'lw-explorer-active';
  const state: ExplorerControlState = {
    x: 0, y: 0, mx: 0, my: 0,
    keyboard: new Set<string>(),
    joy: false,
    joyId: null
  };
  const listeners: Off[] = [];

  const on = (
    target: EventTarget | null,
    type: string,
    fn: EventListener,
    eventOptions?: AddEventListenerOptions | boolean
  ): void => {
    if (!target) return;
    target.addEventListener(type, fn, eventOptions);
    listeners.push(() => target.removeEventListener(type, fn, eventOptions));
  };

  const updateVector = (): ExplorerControlState => {
    let x = 0;
    let y = 0;
    if (state.keyboard.has('ArrowLeft') || state.keyboard.has('KeyA')) x--;
    if (state.keyboard.has('ArrowRight') || state.keyboard.has('KeyD')) x++;
    if (state.keyboard.has('ArrowUp') || state.keyboard.has('KeyW')) y--;
    if (state.keyboard.has('ArrowDown') || state.keyboard.has('KeyS')) y++;
    x += state.x;
    y += state.y;
    const magnitude = Math.hypot(x, y) || 1;
    state.mx = Math.abs(x) + Math.abs(y) > 0.01 ? x / magnitude : 0;
    state.my = Math.abs(x) + Math.abs(y) > 0.01 ? y / magnitude : 0;
    return state;
  };

  if (joystick) {
    const setJoystickGestureLock = (locked: boolean): void => {
      document.documentElement.classList.toggle('lw-joystick-gesture-lock', locked);
      document.body.classList.toggle('lw-joystick-gesture-lock', locked);
    };

    const point = (clientX: number, clientY: number): void => {
      const rect = joystick.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const radius = Math.max(1, rect.width * 0.34);
      let dx = clientX - cx;
      let dy = clientY - cy;
      const magnitude = Math.hypot(dx, dy) || 1;
      if (magnitude > radius) {
        dx = (dx / magnitude) * radius;
        dy = (dy / magnitude) * radius;
      }
      state.x = dx / radius;
      state.y = dy / radius;
      if (knob) knob.style.transform = `translate(${dx}px,${dy}px)`;
    };

    on(joystick, 'pointerdown', ((event: Event) => {
      const e = event as PointerEvent;
      e.preventDefault();
      if (state.joy && state.joyId !== e.pointerId) return;
      state.joy = true;
      state.joyId = e.pointerId;
      setJoystickGestureLock(true);
      joystick.setPointerCapture?.(e.pointerId);
      point(e.clientX, e.clientY);
    }) as EventListener);
    on(joystick, 'pointermove', ((event: Event) => {
      const e = event as PointerEvent;
      if (state.joy && e.pointerId === state.joyId) {
        e.preventDefault();
        point(e.clientX, e.clientY);
      }
    }) as EventListener);
    const stop = ((event: Event): void => {
      const e = event as PointerEvent;
      if (e.pointerId !== state.joyId) return;
      e.preventDefault();
      state.joy = false;
      state.joyId = null;
      state.x = 0;
      state.y = 0;
      setJoystickGestureLock(false);
      if (knob) knob.style.transform = 'translate(0,0)';
    }) as EventListener;
    on(joystick, 'pointerup', stop);
    on(joystick, 'pointercancel', stop);
    on(joystick, 'lostpointercapture', stop);

    // iOS Safari can still begin a page pinch when another finger lands while
    // the joystick thumb is already down. Guard only during an active joystick
    // drag so modules that intentionally use pinch elsewhere remain unaffected.
    const preventGestureWhileDriving = ((event: Event): void => {
      if (!state.joy) return;
      event.preventDefault();
    }) as EventListener;
    const preventMultiTouchWhileDriving = ((event: Event): void => {
      if (!state.joy) return;
      const touch = event as TouchEvent;
      if (touch.touches.length > 1) event.preventDefault();
    }) as EventListener;
    const nonPassiveCapture: AddEventListenerOptions = { passive: false, capture: true };
    on(document, 'touchstart', preventMultiTouchWhileDriving, nonPassiveCapture);
    on(document, 'touchmove', preventMultiTouchWhileDriving, nonPassiveCapture);
    on(document, 'gesturestart', preventGestureWhileDriving, nonPassiveCapture);
    on(document, 'gesturechange', preventGestureWhileDriving, nonPassiveCapture);
    on(document, 'gestureend', preventGestureWhileDriving, nonPassiveCapture);

    on(joystick, 'contextmenu', ((event: Event) => event.preventDefault()) as EventListener);
    on(joystick, 'dragstart', ((event: Event) => event.preventDefault()) as EventListener);
  }

  on(actionButton, 'contextmenu', ((event: Event) => event.preventDefault()) as EventListener);
  on(actionButton, 'dragstart', ((event: Event) => event.preventDefault()) as EventListener);
  on(document, 'selectstart', ((event: Event) => {
    if (document.body.classList.contains(activeClass)) event.preventDefault();
  }) as EventListener);
  on(document, 'contextmenu', ((event: Event) => {
    const target = event.target as Element | null;
    if (document.body.classList.contains(activeClass) && target?.closest?.('.lw-joystick,.lw-action,canvas')) {
      event.preventDefault();
    }
  }) as EventListener);

  const movementKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'KeyA', 'KeyD', 'KeyW', 'KeyS']);
  on(document, 'keydown', ((event: Event) => {
    const e = event as KeyboardEvent;
    if (movementKeys.has(e.code)) state.keyboard.add(e.code);
    if ((e.code === 'Space' || e.code === 'KeyE') && options.onAction) {
      e.preventDefault();
      options.onAction();
    }
  }) as EventListener);
  on(document, 'keyup', ((event: Event) => { state.keyboard.delete((event as KeyboardEvent).code); }) as EventListener);
  on(actionButton, 'click', (() => options.onAction?.()) as EventListener);

  const setAction = (action: ExplorerAction | null): void => {
    if (!actionButton) return;
    actionButton.classList.remove('visible', 'action-enter', 'action-follow', 'action-return', 'action-touch', 'action-observe');
    if (!action) {
      actionButton.textContent = '';
      return;
    }
    actionButton.textContent = action.label || '';
    actionButton.classList.add('visible', `action-${action.kind ?? 'observe'}`);
  };

  const setActive = (flag: boolean): void => { document.body.classList.toggle(activeClass, flag); };
  const clearGestureLock = (): void => {
    document.documentElement.classList.remove('lw-joystick-gesture-lock');
    document.body.classList.remove('lw-joystick-gesture-lock');
  };
  const reset = (): void => {
    state.x = state.y = state.mx = state.my = 0;
    state.keyboard.clear();
    state.joy = false;
    state.joyId = null;
    clearGestureLock();
    if (knob) knob.style.transform = 'translate(0,0)';
  };

  return {
    state,
    updateVector,
    setAction,
    setActive,
    reset,
    destroy() { clearGestureLock(); listeners.splice(0).forEach((off) => off()); }
  };
}
